import {Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import type {EnergyBalanceResponse} from '../../../../../api/models/energy-balance-response';
import type {Chart, LegendItem} from 'chart.js';
import {ChartData, ChartOptions} from 'chart.js';
import {BaseChartComponent, BalanceDataRow} from '../base-chart.component';
import {BaseChartDirective} from 'ng2-charts';

import {MatIcon} from '@angular/material/icon';

@Component({
  selector: 'app-daily-balance-chart',
  templateUrl: './daily-balance-chart.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective, MatIcon],
})
export class DailyBalanceChartComponent extends BaseChartComponent implements OnChanges {
  @Input() data!: EnergyBalanceResponse;
  isDailyAccordionOpen: any;    //specially for the day-Details! close it on the day screen, see #189
  useCorrectedValues = false;
  private readonly useCompactTooltip = typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;

  barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        display: true,
        grid: {
          display: false,
        },
        border: {display: false},
      },
      y: {
        display: true,
        stacked: true,
        grid: {
          display: true,
        },
        ticks: {
          stepSize: 50,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          generateLabels(chart: Chart): LegendItem[] {
            function customText(i: number) {
              if (i == 0) {
                //set labels manually for the two columns
                return 'Gesamt-Produktion';
              } else {
                return 'Gesamt-Verbrauch';
              }
            }

            return chart.data.datasets
              .map((dataset, i) => ({
                text: customText(i), // overwrite the textes for the legend labels
                fillStyle: dataset.backgroundColor as string,
                strokeStyle: dataset.borderColor as string,
                lineWidth: 0,
                boxWidth: 30,
                hidden: false,
                datasetIndex: i,
              }))
              .filter((_, i) => i % 3 === 0); //dont show all 4 labels, but only two for the two columsn
          },
        },
      },
      tooltip: {
        enabled: true,
        mode: this.useCompactTooltip ? 'nearest' : 'index',
        intersect: false,
        position: 'nearest',
        backgroundColor: 'rgba(0, 0, 0, 0.38)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.25)',
        borderWidth: 1,
        padding: this.useCompactTooltip ? 6 : 10,
        bodySpacing: this.useCompactTooltip ? 3 : 6,
        boxPadding: this.useCompactTooltip ? 2 : 4,
        titleFont: {
          size: this.useCompactTooltip ? 10 : 12,
        },
        bodyFont: {
          size: this.useCompactTooltip ? 10 : 12,
        },
        caretPadding: this.useCompactTooltip ? 8 : 12,
        displayColors: !this.useCompactTooltip,
        callbacks: {
          title: () => '', // removes label-title ("Energiebilanz")
          label: (context) => context.dataset.label + ': ' + context.formattedValue + ' kWh',
        },
      },
    },
  };

  constructor() {
    super();
    this.isDailyAccordionOpen = false;
  }

  toggleDailyDetailsAccordion() {
    this.isDailyAccordionOpen = !this.isDailyAccordionOpen;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    if (!this.data) {
      return;
    }
    this.chartOptions.set(this.barChartOptions);
    this.chartData.set(this.getChartData());
    this.balanceData = [super.getBalanceDataRow(this.data, this.useCorrectedValues)];
    this.independenceData = [super.getIndependenceDataRow(this.data, this.useCorrectedValues)];
    this.chart?.update();
  }

  getChartData(): ChartData<'bar'> {
    const selfConsumptionC = this.getSelfConsumptionFromConsumption(this.data, this.useCorrectedValues);
    const fromBattery = this.getEnergyFromBattery(this.data, this.useCorrectedValues);
    const fromGrid = this.data?.energyFromGrid ?? 0;

    const selfConsumptionP = this.getSelfConsumptionFromProduction(this.data, this.useCorrectedValues);
    const toBattery = this.data?.energyToBattery ?? 0;
    const toGrid = this.getEnergyToGrid(this.data, this.useCorrectedValues);
    const barWidth = 0.9; // slightly less than 1 to ensure visibility
    const barThickness = 50; // slightly less than 1 to ensure visibility
    const categorySpacing = 0.5; // small gap to ensure bars are visible

    return {
      labels: ['Energiebilanz'],
      datasets: [
        {
          label: 'Eigenverbrauch (Produktion)',
          data: [selfConsumptionP],
          backgroundColor: '#FBBA00',
          stack: 'production',
          barThickness: barThickness,
          categoryPercentage: categorySpacing,

          borderSkipped: 'middle',
        },
        {
          label: 'Zur Batterie',
          data: [toBattery],
          backgroundColor: '#FBBA00',
          stack: 'production',
          barThickness: barThickness,
          categoryPercentage: categorySpacing,
        },
        {
          label: 'Verkauf',
          data: [toGrid],
          backgroundColor: '#FBBA00',
          stack: 'production',
          barThickness: barThickness,
          categoryPercentage: categorySpacing,
        },
        {
          label: 'Aus Batterie',
          data: [-fromBattery],
          backgroundColor: '#00B6EE',
          stack: 'consumption',
          barThickness: barThickness,
          categoryPercentage: categorySpacing,
        },
        {
          label: 'Einkauf',
          data: [-fromGrid],
          backgroundColor: '#00B6EE',
          stack: 'consumption',
          barThickness: barThickness,
          categoryPercentage: categorySpacing,
        },
        {
          label: 'Eigenverbrauch (Verbrauch)',
          data: [selfConsumptionC],
          backgroundColor: '#00B6EE',
          stack: 'consumption',
          barThickness: barThickness,
          categoryPercentage: categorySpacing,
        },
      ],
    };
  }
}
